import { useMemo } from 'react';

import { env } from '~/lib/env';
import type { variables } from '~/lib/variables';
import { ErrorContent } from '~/modules/errors/components/ErrorContent';

interface IFrameProps {
  urlEnvKey: keyof typeof variables;
  ariaLabel: string;
}

/**
 * Used to load an iframe and display the results in a div that is styled to take
 * up the full height and width of its parent. If something goes wrong while loading
 * the iframe, an ErrorContent component is displayed instead.
 *
 * @param urlEnvKey - The environment variable key for the iframe URL.
 * @param ariaLabel - The aria label for the iframe.
 * @returns {React.ReactNode} - A div containing either the iframe or an ErrorContent component.
 */
export function Iframe({ urlEnvKey, ariaLabel }: IFrameProps) {
  // Memoize the iframe so that we don't get a whole bunch of funky re-rendering
  // in React.StrictMode.
  const frame = useMemo(() => {
    // Wrapping this just in case the URL is misconfigured and causes an exception.
    // Better to display an error than to show nothing on the screen.
    try {
      const url = new URL(env(urlEnvKey));

      return (
        <iframe
          aria-label={ariaLabel}
          src={url.toString()}
          style={{
            // References for iframe styling:
            // https://stackoverflow.com/questions/5867985/full-screen-iframe-with-a-height-of-100
            // https://anjitpariyar.medium.com/make-iframe-height-dynamic-based-on-content-inside-using-javascript-c675e78e4dd3
            height: '100%',
            width: '100%',
            border: 'none',
          }}
        />
      );
    } catch (e) {
      console.error('Error loading dashboard', e);

      return null;
    }
  }, [ariaLabel, urlEnvKey]);

  return (
    <div className="h-full">
      {frame ?? <ErrorContent type="default" variant="inline" />}
    </div>
  );
}

Iframe.displayName = 'Iframe';
